import { IonBadge, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonGrid, IonIcon, IonRow, IonSpinner, useIonToast } from '@ionic/react';
import { document, people, cart, cash, timeOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { apiBaseUrl, appSubDir } from '../../configs/app';
import { useHistory } from 'react-router';
import { getBadgeColor } from '../../services/account/invoices.service';
import loginService from '../../services/loginService';
import './AdminPages.scss';

const BDT = new Intl.NumberFormat('en-IN');

interface DashboardStats {
    total_orders: number;
    total_users: number;
    pending_orders: number;
    processing_orders: number;
    total_revenue: number;
    total_due: number;
    recent_orders: {
        invoice_id: string;
        status: string;
        total: number;
        paid: number;
        due: number;
        created_at: string;
    }[];
    orders_by_status: { status: string; count: number }[];
}

const AdminDashboard: React.FC = () => {
    const history = useHistory();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [clearingCache, setClearingCache] = useState(false);
    const [presentToast] = useIonToast();

    function clearCache() {
        setClearingCache(true);
        const token = loginService.currentUser.token;
        fetch(apiBaseUrl + 'dashboard/clear-cache', {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                Accept: 'application/json',
            },
        })
            .then((res) => res.json())
            .then(() => presentToast({ message: 'Cache cleared successfully', duration: 2500, color: 'success' }))
            .catch(() => presentToast({ message: 'Failed to clear cache', duration: 2500, color: 'danger' }))
            .finally(() => setClearingCache(false));
    }

    useEffect(() => {
        const token = loginService.currentUser.token;
        fetch(apiBaseUrl + 'dashboard', {
            headers: {
                Authorization: 'Bearer ' + token,
                Accept: 'application/json',
            },
        })
            .then((res) => {
                if (!res.ok) throw new Error('Unauthorized');
                return res.json();
            })
            .then((data) => setStats(data))
            .catch(() => setStats(null))
            .finally(() => setLoading(false));
    }, []);

    const StatCard = ({ title, value, icon, color, sub }: any) => (
        <IonCard className="admin-stat-card" style={{ margin: '10px' }}>
            <IonCardHeader>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <IonCardTitle style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>{title}</IonCardTitle>
                    <IonIcon icon={icon} style={{ fontSize: '32px', color }} />
                </div>
            </IonCardHeader>
            <IonCardContent>
                <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#1e293b' }}>{value}</h1>
                {sub && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>{sub}</p>}
            </IonCardContent>
        </IonCard>
    );

    return (
        <AdminLayout title="Dashboard">
            {loading ? (
                <div style={{ textAlign: 'center', padding: '80px' }}>
                    <IonSpinner />
                </div>
            ) : stats ? (
                <IonGrid>
                    {/* Stat Cards */}
                    <IonRow>
                        <IonCol size="12" sizeMd="6" sizeLg="3">
                            <StatCard
                                title="Total Orders"
                                value={stats.total_orders}
                                icon={document}
                                color="var(--ion-color-primary)"
                            />
                        </IonCol>
                        <IonCol size="12" sizeMd="6" sizeLg="3">
                            <StatCard
                                title="Total Users"
                                value={stats.total_users}
                                icon={people}
                                color="var(--ion-color-secondary)"
                            />
                        </IonCol>
                        <IonCol size="12" sizeMd="6" sizeLg="3">
                            <StatCard
                                title="Pending Orders"
                                value={stats.pending_orders}
                                icon={cart}
                                color="var(--ion-color-warning)"
                                sub={`${stats.processing_orders} in processing`}
                            />
                        </IonCol>
                        <IonCol size="12" sizeMd="6" sizeLg="3">
                            <StatCard
                                title="Total Revenue"
                                value={`৳${BDT.format(stats.total_revenue)}`}
                                icon={cash}
                                color="var(--ion-color-success)"
                                sub={`৳${BDT.format(stats.total_due)} due`}
                            />
                        </IonCol>
                    </IonRow>

                    {/* Recent Orders + Orders by Status */}
                    <IonRow>
                        <IonCol size="12" sizeLg="8">
                            <IonCard className="admin-card" style={{ margin: '10px' }}>
                                <IonCardHeader>
                                    <IonCardTitle style={{ display: 'flex', alignItems: 'center', color: '#1e293b', fontSize: '16px' }}>
                                        <IonIcon icon={timeOutline} style={{ marginRight: '8px' }} />
                                        Recent Orders
                                    </IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    <div className="admin-table-container" style={{ padding: 0, boxShadow: 'none' }}>
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Order ID</th>
                                                    <th>Date</th>
                                                    <th>Total</th>
                                                    <th>Due</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(stats.recent_orders ?? []).map((order, i) => (
                                                    <tr
                                                        key={i}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => history.push(appSubDir + 'admin/orders/' + order.invoice_id)}
                                                    >
                                                        <td className="text-center">#{order.invoice_id}</td>
                                                        <td className="text-center">{new Date(Date.parse(order.created_at)).toDateString()}</td>
                                                        <td className="text-end">৳{BDT.format(order.total)}</td>
                                                        <td className="text-end">{order.due ? `৳${BDT.format(order.due)}` : '—'}</td>
                                                        <td className="text-center">
                                                            <IonBadge color={getBadgeColor(order.status)}>{order.status}</IonBadge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>

                        <IonCol size="12" sizeLg="4">
                            <IonCard className="admin-card" style={{ margin: '10px' }}>
                                <IonCardHeader>
                                    <IonCardTitle style={{ color: '#1e293b', fontSize: '16px' }}>Orders by Status</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    {(stats.orders_by_status ?? []).map((item, i) => (
                                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <IonBadge color={getBadgeColor(item.status)} style={{ fontSize: '13px', padding: '6px 10px' }}>
                                                {item.status}
                                            </IonBadge>
                                            <strong style={{ color: '#1e293b' }}>{item.count}</strong>
                                        </div>
                                    ))}
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>
                </IonGrid>
            ) : (
                <div style={{ textAlign: 'center', padding: '80px', color: '#64748b' }}>
                    <h2>Failed to load dashboard data</h2>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
                <IonButton fill="outline" color="danger" onClick={clearCache} disabled={clearingCache}>
                    {clearingCache ? <IonSpinner name="lines-small" /> : <IonIcon icon={trashOutline} slot="start" />}
                    Clear Cache
                </IonButton>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
